/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React from "react";
import {
  Image,
  Card,
  Container,
  Navbar,
  Row,
  Col,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Button,
} from "react-bootstrap";
import { HashLink } from "react-router-hash-link";
import moment from "moment-timezone";
import { toHeaderCase } from "js-convert-case";
import { SOCIAL_MEDIA_ICON } from "./socialMedia.icon";
export interface IFooterLinkProps {
  name: string;
  link: string;
  target?: "_blank" | "_self";
}

export interface IFooterProps {
  footerBrandLink?: string;
  footerBrandLogo: string;
  footerBrandTitle: string;
  footerBrandTitleStyle?: any;
  footerBrandDescription?: string;
  footerUsefulLinkLabel?: string;
  footerUsefulLinks?: IFooterLinkProps[];
  footerServiceLinkLabel?: string;
  footerServiceLinks?: IFooterLinkProps[];
  footerContactLabel?: string;
  footerContactLinks?: {
    name: string;
    value: string;
  }[];
  footerCopyright: {
    brandTitle: string;
    brandLogo: string;
    brandLogoHeight?: number;
    brandLink: string;
    copyrightTitle: string;
    startYear: string;
    target?: "_blank" | "_self";
    subBrandLogo?: string;
    vlinderLogo?: string;
    vlinderLink?: string;
    vlinderName?: string;
  };
  socialLinks?: {
    name:
      | "facebook"
      | "twitter"
      | "instagram"
      | "linkedin"
      | "youtube"
      | "website";
    link: string;
  }[];
}

export const Footer = ({
  footerBrandLogo,
  footerBrandDescription,
  footerBrandLink,
  footerBrandTitle,
  footerBrandTitleStyle,
  footerUsefulLinkLabel,
  footerUsefulLinks,
  footerServiceLinks,
  footerServiceLinkLabel,
  footerContactLabel,
  footerContactLinks,
  footerCopyright,
  socialLinks,
}: IFooterProps) => {
  const currentYear = moment().get("year");

  const getFirstLetterOfEachWord = (text: string) =>
    text?.match(/\b\w/g)?.join("");

  const isValidUrl = (urlString: string) => {
    try {
      return Boolean(new URL(urlString));
    } catch (e) {
      return false;
    }
  };

  function isValidHttpUrl(string: string) {
    let url;

    try {
      url = new URL(string);
    } catch (_) {
      return false;
    }

    return url.protocol === "http:" || url.protocol === "https:";
  }

  const _getIcon = (Icon: any) => {
    let _icon: any = [];
    if (typeof Icon === "string") {
      if (isValidUrl(Icon)) {
        _icon.push(
          <Image
            key={`image-icon`}
            src={Icon}
            width={20}
            height={20}
            style={{ objectFit: "contain" }}
          />
        );
      } else {
        const HeroIcon = require("@heroicons/react/outline");
        const NavIcon = HeroIcon[Icon];
        if (NavIcon) {
          _icon.push(
            <NavIcon
              key={`hero-icon`}
              src={Icon}
              className="icon icon-xs text-white"
            />
          );
        }
      }
      return _icon;
    }
    if (Icon) {
      _icon.push(
        <Icon key={`hero-icon-2`} className="icon icon-xs text-white" />
      );
    }
    return _icon;
  };

  const _renderSocialMediaIconIf = () => {
    const _comp: any = [];
    if (socialLinks) {
      for (const [index, socialLink] of socialLinks?.entries()) {
        _comp.push(
          <ButtonGroup
            key={`profile-card-smi-${index}`}
            className="mb-3 me-3 mb-md-0  d-md-inline-flex"
          >
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip className="m-0">
                  {toHeaderCase(socialLink?.name ?? "")}
                </Tooltip>
              }
            >
              <Button
                variant="transparent"
                className={"bg-white p-1"}
                onClick={() => window.open(socialLink.link, "__blank")}
              >
                {_getIcon(SOCIAL_MEDIA_ICON[socialLink.name]?.icon)}
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        );
      }
    }
    return _comp;
  };

  const _renderBrand = () => {
    return (
      <Col md={5}>
        <Navbar.Brand
          as={HashLink}
          to={
            isValidHttpUrl(footerBrandLink)
              ? { pathname: footerBrandLink }
              : footerBrandLink
          }
          target={isValidHttpUrl(footerBrandLink) ? "_blank" : "_self"}
          className="me-lg-3 mb-3 d-flex align-items-center"
        >
          <Image
            src={footerBrandLogo}
            style={{ height: "30px", width: "90px" }}
          />
          <span className="ms-2 brand-text" style={footerBrandTitleStyle}>
            {footerBrandTitle}
          </span>
        </Navbar.Brand>
        {/* {footerBrandDescription && (
          <p style={{ fontSize: "12px", marginLeft: 0 }}>
            {footerBrandDescription}
          </p>
        )} */}
        <div className="d-block">{_renderSocialMediaIconIf()}</div>
      </Col>
    );
  };

  const _renderUsefulLinks = () => {
    return (
      <Col xs={6} md={2} className="mb-5 mb-lg-0">
        {/* {footerUsefulLinkLabel && (
          <span style={{ fontSize: "16px" }}>{footerUsefulLinkLabel}</span>
        )}
        {footerUsefulLinks && (
          <ul className="links-vertical mt-2">
            {footerUsefulLinks?.map((item, index) => (
              <li
                key={`footer-list-${index}`}
                style={{ fontSize: "12px", marginLeft: 0 }}
              >
                <Card.Link
                  target={item?.target ? "_blank" : "_self"}
                  href={item?.link}
                  style={{ padding: 0 }}
                >
                  {item?.name}
                </Card.Link>
              </li>
            ))}
          </ul>
        )} */}
      </Col>
    );
  };

  const _renderServiceLinks = () => {
    return (
      <Col xs={6} md={2} className="mb-5 mb-lg-0">
        {/* {footerServiceLinkLabel && (
          <span style={{ fontSize: "16px" }}>{footerServiceLinkLabel}</span>
        )}
        {footerServiceLinks && (
          <ul className="links-vertical mt-2">
            {footerServiceLinks?.map((item, index) => (
              <li
                key={`footer-list-${index}`}
                style={{ fontSize: "12px", marginLeft: 0 }}
              >
                <Card.Link
                  style={{ padding: 0 }}
                  target={item?.target ? "_blank" : "_self"}
                  href={item?.link}
                >
                  {item?.name}
                </Card.Link>
              </li>
            ))}
          </ul>
        )} */}
      </Col>
    );
  };

  const _renderAddress = () => {
    return (
      <Col xs={6} md={3} className="mb-5 mb-lg-0">
        {footerContactLabel && (
          <span style={{ fontSize: "16px" }}>{footerContactLabel}</span>
        )}
        {footerContactLinks && (
          <ul className="links-vertical mt-2" style={{ fontSize: "12px" }}>
            {footerContactLinks?.map((item, index) => (
              <li
                key={`footer-list-${index}`}
                style={{ fontSize: "12px", marginLeft: 0 }}
              >
                {item?.name} {item?.value}
              </li>
            ))}
          </ul>
        )}
      </Col>
    );
  };

  const _renderHorizontal = () => {
    return <hr className="bg-gray-600 my-2" />;
  };

  const _renderFooterCopyRight = () => {
    const currentYear = moment().get("year");

    return (
      <Row>
        <Card.Link
          href={footerCopyright?.brandLink}
          target={footerCopyright?.target ?? "_blank"}
          className="d-flex justify-content-center"
        >
          <Image
            src={footerCopyright?.brandLogo}
            height={footerCopyright?.brandLogoHeight ?? 30}
            className="d-block mb-2"
          />
          <p
            style={{
              fontSize: "13px",
              marginLeft: "0.5%",
              marginRight: "0.5%",
              marginTop: "1%",
              color: "whitesmoke",
            }}
            className="text-center font-weight-normal font-small"
          >
            by
          </p>
          <a href={footerCopyright?.brandLink} target="_blank">
            <Image
              src={footerCopyright?.subBrandLogo}
              height={35}
              className="d-block mb-2"
            />
          </a>
        </Card.Link>
        <Col>
          <p
            style={{
              color: "whitesmoke",
              fontSize: "15px",
            }}
            className="mb-0 text-center  font-weight-normal font-small"
          >
            Copyright ©
            {` ${footerCopyright.copyrightTitle} ${footerCopyright.startYear}`}-
            {`${currentYear} `}. Powered by
            <a
              href={footerCopyright?.vlinderLink}
              style={{
                color: "whitesmoke",
              }}
              target="_blank"
            >
              {` ${footerCopyright?.vlinderName}`}
            </a>
            <a href={footerCopyright?.vlinderLink} target="_blank">
              <Image
                src={footerCopyright?.vlinderLogo}
                height={30}
                style={{ padding: "5px", marginTop: "-5px" }}
              />
            </a>
          </p>
        </Col>
      </Row>
    );
  };

  return (
    <footer className="footer py-3 bg-primary text-white">
      <Container>
        <Row>
          {_renderBrand()}
          {_renderUsefulLinks()}
          {_renderServiceLinks()}
          {_renderAddress()}
        </Row>
        {_renderHorizontal()}
        {_renderFooterCopyRight()}
      </Container>
    </footer>
  );
};

Footer.defaultProps = {
  footerBrandLink: "#root",
};
